<div class="bg-page flex h-full w-full flex-col overflow-hidden">
  <div
    class="bg-elevated border-b-default flex shrink-0 items-center gap-3 border-b px-3.5 py-2">
    <div class="min-w-0">
      <div class="text-sm font-semibold tracking-tight whitespace-nowrap">
        {{ datePreview() }}
      </div>
    </div>

    <div class="gx-segmented">
      @for (segment of viewSegments(); track segment.view) {
        <button
          type="button"
          class="seg"
          [class.is-active]="activeView() === segment.view"
          (click)="changeView(segment.view)">
          {{ segment.label }}
        </button>
      }
    </div>

    <div class="flex-1"></div>

    <button class="btn btn-cancel btn-sm" (click)="returnToday()">
      {{ 'common.calendar.today' | translate }}
    </button>
    <div class="flex gap-0.5">
      <button
        class="btn btn-icon"
        [attr.aria-label]="'common.previous' | translate"
        [title]="'common.previous' | translate"
        (click)="switchToPrevious()">
        <i [class]="ICONS.caretLeft" class="text-sm"></i>
      </button>
      <button
        class="btn btn-icon"
        [attr.aria-label]="'common.next' | translate"
        [title]="'common.next' | translate"
        (click)="switchToNext()">
        <i [class]="ICONS.caretRight" class="text-sm"></i>
      </button>
    </div>
  </div>

  <app-calendar-legend [eventCount]="visibleEventCount()" />

  @if (activeView() === PossibleViews.MONTH) {
    <div class="bg-surface flex min-h-0 flex-1 flex-col overflow-auto">
      <div class="bg-elevated border-b-default sticky top-0 z-4 grid grid-cols-7 border-b">
        @for (day of weekDays(); track trackWeekDayString($index, day); let i = $index) {
          <div
            class="text-text-tertiary py-1.5 text-center text-[10px] font-semibold tracking-[0.06em] uppercase"
            [class.border-r-weak]="i < 6"
            [class.border-r]="i < 6">
            {{ day }}
          </div>
        }
      </div>

      <div class="grid flex-1 auto-rows-[minmax(104px,1fr)] grid-cols-7">
        @for (day of monthDaysEnriched(); track trackMonthDay($index, day)) {
          <div
            class="border-r-weak border-b-weak flex min-w-0 flex-col gap-[3px] border-r border-b p-[5px]"
            [class.bg-page]="day.isOutsideMonth"
            [class.bg-surface]="!day.isOutsideMonth && day.isWeekend"
            [class.bg-elevated]="!day.isOutsideMonth && !day.isWeekend">
            <span
              class="self-start rounded-full py-px font-mono text-[11.5px] font-semibold"
              [class]="
                day.isToday
                  ? 'bg-accent-main text-text-on-accent px-1.5'
                  : day.isOutsideMonth
                    ? 'text-text-tertiary px-0.5'
                    : 'text-text-secondary px-0.5'
              ">
              {{ day.day | date: 'd' }}
            </span>

            @for (event of day.visibleMarkers; track trackMonthMarker($index, event)) {
              <div
                #monthEvent
                class="flex min-w-0 items-center gap-1 rounded-xs px-1 py-px"
                [style.background]="event.fill"
                [style.border-left]="'3px solid ' + event.color">
                <span class="shrink-0 font-mono text-[9.5px] opacity-80">
                  {{ event.event.start | date: 'HH:mm' }}
                </span>
                <span class="min-w-0 truncate text-[11px] font-medium">
                  {{ event.event.title }}
                </span>
                <div class="flex-1"></div>
                @if (event.event.cutoff) {
                  <span
                    class="inline-flex shrink-0 items-center gap-0.5 font-mono text-[9px]"
                    [style.color]="event.ink">
                    <i [class]="ICONS.timer" class="text-[10px]"></i>
                    {{ event.event.cutoff | date: 'HH:mm' }}
                  </span>
                }
                @if (event.event.recurrence) {
                  <i [class]="ICONS.repeat" class="shrink-0 text-[10px] opacity-55"></i>
                }
                <app-tooltip
                  [target]="monthEvent"
                  [position]="'top'"
                  [interactive]="true"
                  [title]="event.event.title"
                  [content]="event.tooltip" />
              </div>
            }

            @if (day.hiddenMarkerCount > 0) {
              <span class="text-text-tertiary pl-[3px] font-mono text-[10.5px]">
                {{
                  'common.calendar.more_events'
                    | translate: { count: day.hiddenMarkerCount }
                }}
              </span>
            }
          </div>
        }
      </div>
    </div>
  } @else {
    @let columns = timeGridDays();
    <div class="bg-surface min-h-0 flex-1 overflow-auto" #gridScroll>
      <div
        class="grid"
        data-calendar-grid
        [style.--calendar-hour-px]="hourPxCss"
        [style.grid-template-columns]="
          'var(--calendar-gutter) repeat(' + columns.length + ', minmax(0, 1fr))'
        ">
        <div
          class="bg-elevated border-b-default border-r-weak sticky top-0 z-11 border-r border-b px-2 py-1.5">
          <span
            class="text-text-tertiary text-[10px] font-semibold tracking-[0.06em] uppercase">
            {{ 'common.calendar.hours' | translate }}
          </span>
        </div>
        @for (day of columns; track trackWeekDay($index, day)) {
          <div
            class="bg-elevated border-b-default border-r-weak sticky top-0 z-10 border-r border-b px-2 py-[5px] text-center">
            <span
              class="inline-flex items-baseline gap-1.5 rounded-full py-0.5"
              [class]="
                day.isToday
                  ? 'bg-accent-main text-text-on-accent px-2'
                  : 'text-text-secondary'
              ">
              <span class="text-[11px] font-semibold tracking-[0.05em] uppercase">
                {{ day.shortLabel }}
              </span>
              <span class="font-mono text-[12.5px] font-semibold">
                {{ day.day | date: 'd' }}
              </span>
            </span>
          </div>
        }

        <div
          class="bg-page border-r-default relative border-r"
          [style.height.px]="dayHeightPx">
          @for (hour of hoursEnriched(); track trackHour($index, hour)) {
            <span
              class="text-text-tertiary absolute right-2 font-mono text-[10.5px]"
              [style.top.px]="hour.labelTop">
              {{ hour.hour }}
            </span>
          }
          @if (showNowLabel()) {
            <span
              class="bg-danger absolute right-1 -translate-y-1/2 rounded-xs px-1 font-mono text-[9.5px] font-semibold text-white"
              [style.top]="nowTimeStepPosition()">
              {{ actualDate() | date: 'HH:mm' }}
            </span>
          }
        </div>

        @for (day of columns; track trackWeekDay($index, day)) {
          <div
            class="calendar-hour-grid border-r-weak relative border-r"
            data-calendar-column
            [class.bg-surface]="day.isWeekend"
            [class.bg-elevated]="!day.isWeekend"
            [style.height.px]="dayHeightPx">
            @for (marker of day.markers; track trackCalendarEvent($index, marker)) {
              <app-calendar-event
                [event]="marker"
                [pixelsPerMinute]="pixelsPerMinute"
                [days]="columnDates()"
                [draggable]="marker.editable ?? false"
                [dragActive]="dragActive()"
                (draggingChange)="dragActive.set($event)"
                (eventChange)="onEventChange($event, !!marker.recurrence)" />
            }

            <app-calendar-time-step
              class="w-full"
              [isToday]="day.isToday"
              [nowTimeStepPosition]="nowTimeStepPosition()" />
          </div>
        }
      </div>
    </div>
  }
</div>

@if (recurrenceEditModalOpen()) {
  <app-modal
    [title]="'common.calendar.edit_recurrence_modal.title' | translate"
    [config]="modalConfig"
    (closeModal)="cancelRecurrenceEdit()">
    <div modal-body class="flex flex-col gap-3 py-1">
      @for (option of recurrenceScopeOptions; track option.value) {
        <button
          type="button"
          [disabled]="option.disabled"
          (click)="recurrenceScope.set(option.value)"
          [attr.aria-pressed]="recurrenceScope() === option.value"
          class="flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors"
          [class]="
            option.disabled
              ? 'border-weak bg-surface cursor-not-allowed opacity-50'
              : recurrenceScope() === option.value
                ? 'border-accent-main bg-accent-interactive cursor-pointer'
                : 'border-weak hover:border-accent-border bg-surface cursor-pointer'
          ">
          <i [class]="option.icon" class="text-accent-main mt-0.5 shrink-0 text-lg"></i>
          <span class="flex flex-col gap-0.5">
            <span class="text-text-primary text-sm font-semibold">{{
              option.label | translate
            }}</span>
            <span class="text-text-secondary text-xs">{{
              option.description | translate
            }}</span>
          </span>
        </button>
      }
    </div>
  </app-modal>
}
